import React, { useEffect, useState } from 'react';
import { Box, Button, Group, Text, Badge } from '@mantine/core';
import { openModal, closeAllModals, modals } from '@mantine/modals';
import { DataTable, DataTableColumn } from 'mantine-datatable';
import { formatDate, formatDateTime } from '../../utils/DateUtil.ts';
import { ParkingBilling } from '../../models/parkingBilling.ts';
import useDownloadBillingReport from '../../hooks/useDownloadBillingReport.tsx';
import useConfirmBilling from '../../hooks/useConfirmBilling.tsx';
import useVoidBilling from '../../hooks/useVoidBilling.tsx';
import { notifications } from '@mantine/notifications';
import sortBy from 'lodash.sortby';
import BatchConfirmBillingModal from '../modal/BatchConfirmBillingModal.tsx';

type UnconfirmedBillingTableProps = {
  data: ParkingBilling[];
  isLoading: boolean;
};

export default function UnconfirmedBillingTable({
  data,
  isLoading,
}: UnconfirmedBillingTableProps) {
  const [selectedRecords, setSelectedRecords] = useState<ParkingBilling[]>([]);

  const { mutate: downloadBillingReport } = useDownloadBillingReport();
  const { mutate: confirmBilling } = useConfirmBilling();
  const { mutate: voidBilling } = useVoidBilling();

  const records = sortBy(data, 'readyDateTime').reverse();

  useEffect(() => {
    // 資料刷新時要清除選取的 rows
    setSelectedRecords([]);
  }, [data]);

  const columns: DataTableColumn<ParkingBilling>[] = [
    {
      title: '對帳單/廠商',
      accessor: 'name',
      render: (row) => (
        <div>
          <Text size="sm">{row.name}</Text>
          <Text size="sm" c="dimmed">
            {row.siteProvider.name}
          </Text>
        </div>
      ),
    },
    {
      title: '對帳期間',
      accessor: 'period',
      render: ({ billDateTimeFrom, billDateTimeTo }) => {
        return `${formatDate(billDateTimeFrom)} ~ ${formatDate(billDateTimeTo)}`;
      },
    },
    {
      title: '匯款金額',
      accessor: 'amount',
      render: ({ summary }) => {
        return summary.totalRemittanceAmount.toLocaleString();
      },
    },
    {
      title: '手續費',
      accessor: 'fee',
      render: ({ summary }) => {
        return summary.totalFeeAmount.toLocaleString();
      },
    },
    {
      title: '廠商開立發票金額',
      accessor: 'inputInvoiceAmount',
      render: ({ summary }) => {
        return summary.inputInvoiceAmount.toLocaleString();
      },
    },
    {
      title: '標籤',
      accessor: 'tags',
      render: (row) => (
        <Group>
          {row.tags?.map((tag) => (
            <Badge key={tag.id} variant="outline">
              {tag.name}
            </Badge>
          ))}
        </Group>
      ),
    },
    {
      title: '產檔時間',
      accessor: 'readyDateTime',
      render: ({ readyDateTime }) => {
        return readyDateTime === undefined
          ? '處理中...'
          : formatDateTime(readyDateTime);
      },
    },
    {
      title: '操作',
      accessor: 'actions',
      render: (row) => (
        <Group>
          <Button
            size="xs"
            color="blue"
            disabled={row.state !== 'READY'}
            onClick={() => {
              const modalId = modals.openContextModal({
                modal: 'confirmModal',
                title: `產生匯款單`,
                innerProps: {
                  modalBody: `確定要產生匯款單 ${row.name} 嗎？`,
                  loading: false,
                  onConfirm: () => {
                    modals.updateContextModal({
                      modalId,
                      innerProps: {
                        loading: true,
                      },
                    });
                    confirmBilling(row.id, {
                      onSuccess: () => {
                        notifications.show({
                          title: '成功',
                          message: `對帳單 ${row.name} 已確認`,
                          color: 'green',
                        });
                        closeAllModals();
                      },
                      onSettled: () => {
                        modals.updateContextModal({
                          modalId,
                          innerProps: {
                            loading: false,
                          },
                        });
                      },
                    });
                  },
                },
              });
            }}
          >
            確認
          </Button>
          <Button
            size="xs"
            variant="outline"
            disabled={row.state !== 'READY'}
            onClick={() => {
              downloadBillingReport(row.id, {
                onSuccess: () => {
                  notifications.show({
                    title: '下載成功',
                    message: `對帳單 ${row.name} 的報表已下載`,
                    color: 'green',
                  });
                },
              });
            }}
          >
            下載報表
          </Button>
          <Button
            size="xs"
            color="red"
            variant="outline"
            disabled={row.state !== 'READY'}
            onClick={() => {
              const modalId = modals.openContextModal({
                modal: 'confirmModal',
                title: `作廢對帳單`,
                innerProps: {
                  modalBody: `確定要作廢對帳單 ${row.name} 嗎？`,
                  loading: false,
                  onConfirm: () => {
                    modals.updateContextModal({
                      modalId,
                      innerProps: {
                        loading: true,
                      },
                    });
                    voidBilling(row.id, {
                      onSuccess: () => {
                        notifications.show({
                          title: '成功',
                          message: `對帳單 ${row.name} 已作廢`,
                          color: 'green',
                        });
                        closeAllModals();
                      },
                      onSettled: () => {
                        modals.updateContextModal({
                          modalId,
                          innerProps: {
                            loading: false,
                          },
                        });
                      },
                    });
                  },
                },
              });
            }}
          >
            作廢
          </Button>
        </Group>
      ),
    },
  ];

  return (
    <Box>
      {selectedRecords.length > 0 && (
        <Group mt="md">
          <Text>已勾選 {selectedRecords.length} 筆對帳單</Text>
          <Button
            onClick={() =>
              openModal({
                title: '批次確認對帳單',
                children: (
                  <BatchConfirmBillingModal
                    billings={selectedRecords}
                    onClose={() => closeAllModals()}
                  />
                ),
              })
            }
          >
            批次確認
          </Button>
        </Group>
      )}
      <DataTable
        idAccessor="id"
        highlightOnHover
        minHeight={150}
        columns={columns}
        records={records}
        selectedRecords={selectedRecords}
        onSelectedRecordsChange={setSelectedRecords}
        isRecordSelectable={(row) => row.state === 'READY'}
        verticalSpacing="xs"
        fetching={isLoading}
        noRecordsText="沒有待確認對帳單"
      />
    </Box>
  );
}
